import React, { useEffect, useState, useRef, useCallback } from "react";
import { Layout, Breadcrumb, Table, Popconfirm, Row, Col, Tabs, Avatar, Input, Button, message, Space } from "antd";
import Api from "../api.js";
import { DeleteOutlined, UserOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import debounce from "lodash/debounce";

const { TextArea } = Input;
const { TabPane } = Tabs;
const { Content } = Layout;

const Comments = () => {
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [active, setActive] = useState("recipe");
    const [pagination, setPagination] = useState({ current: 1, pageSize: 25, total: 0 });
    const [reply, setReply] = useState(false);
    const [search, setSearch] = useState("");
    const inputRef = useRef(null);

    // Debounced search function to avoid excessive API calls
    const debouncedSearch = useCallback(
        debounce((value) => fetchComments(1, value), 500),
        [active, pagination.pageSize]
    );

    useEffect(() => {
        fetchComments(1, search);
    }, [active]);

    const fetchComments = (page = 1, searchTerm = "") => {
        setLoading(true);
        Api.get(`/admin/comments`, {
            type: active,
            per_page: pagination.pageSize,
            page,
            search: searchTerm,
        })
            .then((res) => {
                setComments(res.data.data);
                setPagination((prev) => ({
                    ...prev,
                    current: res.data.current_page,
                    total: res.data.total,
                }));
            })
            .finally(() => setLoading(false));
    };

    const handleTableChange = (pag) => {
        fetchComments(pag.current, search);
    };

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearch(value);
        debouncedSearch(value);
    };

    const deleteComment = (comment) => {
        Api.delete(`/admin/comments/${comment.id}`).then(() => {
            message.success("Comment deleted");
            fetchComments(pagination.current, search);
        });
    };

    const createComment = () => {
        const value = inputRef.current.resizableTextArea.textArea.value;
        if (!value) return message.error("Comment required");

        setLoading(true);
        const data = {
            body: value,
            commentable_type: reply.type,
            commentable_id: reply.commentable_id,
            parent_id: reply.id,
        };

        Api.post("/v2/comment/store", data).then(() => {
            setReply(false);
            fetchComments(1, search);
        });
    };

    const renderReplyEditor = () => (
        <Row gutter={8} style={{ marginTop: 8 }}>
            <Col span={18}>
                <TextArea rows={3} placeholder="Comment here" ref={inputRef} />
            </Col>
            <Col>
                <Button type="primary" loading={loading} onClick={createComment}>
                    Add
                </Button>
                <Button onClick={() => setReply(false)} style={{ marginLeft: 8 }}>
                    Cancel
                </Button>
            </Col>
        </Row>
    );

    const getCommentUrl = (comment) => {
        Api.get(`/admin/comments/${comment.id}/url`).then((res) => {
            window.open(res.data.url, "_blank");
        });
    };

    const columns = [
        {
            title: "User",
            dataIndex: "user",
            width: 160,
            render: (user) =>
                user ? (
                    <Link to={`/user/${user.user_id}`}>
                        <Avatar src={user.avatar} icon={<UserOutlined />} /> {user.name_f}
                    </Link>
                ) : (
                    "Unknown"
                ),
        },
        {
            title: "Comment",
            dataIndex: "body",
            render: (text, record) => (
                <div>
                    <div>{text}</div>
                    {reply && reply.id === record.id ? renderReplyEditor() : (
                        <Button size="small" onClick={() => setReply({ ...record, type: active })}>
                            Reply
                        </Button>
                    )}
                </div>
            ),
        },
        {
            title: "Item",
            dataIndex: "commentable_id",
            render: (id, record) => {
                switch (active) {
                    case "trainingvideo":
                        return <Link to={`/training-videos/${id}`}>#{id}</Link>;
                    case "blog":
                    case "testimonial":
                        return <Button onClick={() => getCommentUrl(record)}>#{id}</Button>;
                    default:
                        return <Link to={`/${active}/${id}`}>#{id}</Link>;
                }
            },
        },
        {
            title: "Date",
            dataIndex: "created_at",
            render: (date) => dayjs(date).format("DD.MM.YYYY HH:mm"),
        },
        {
            title: "Action",
            render: (_, record) => (
                <Popconfirm
                    title="Are you sure you want to delete this comment?"
                    onConfirm={() => deleteComment(record)}
                    okText="Yes"
                    cancelText="No"
                >
                    <Button type="link" danger icon={<DeleteOutlined />}>
                        Delete
                    </Button>
                </Popconfirm>
            ),
        },
    ];

    return (
        <Content style={{ margin: "0 16px" }}>
            <Breadcrumb style={{ margin: "16px 0" }}>
                <Breadcrumb.Item>Fitlap</Breadcrumb.Item>
                <Breadcrumb.Item>Comments</Breadcrumb.Item>
            </Breadcrumb>

            <Row>
                <Col span={24}>
                    <div className="site-layout-card">
                        <h1>Comments</h1>

                        {/* Search input */}
                        <Space style={{ marginBottom: 16 }}>
                            <Input.Search
                                placeholder="Search by comment or user"
                                allowClear
                                value={search}
                                onChange={handleSearchChange}
                                style={{ width: 300 }}
                            />
                        </Space>

                        <Tabs defaultActiveKey="recipe" onChange={(key) => setActive(key)}>
                            <TabPane tab="Recipe" key="recipe">
                                <Table
                                    rowKey="id"
                                    columns={columns}
                                    dataSource={comments}
                                    loading={loading}
                                    pagination={pagination}
                                    onChange={handleTableChange}
                                />
                            </TabPane>
                            <TabPane tab="Testimonial" key="testimonial">
                                <Table
                                    rowKey="id"
                                    columns={columns}
                                    dataSource={comments}
                                    loading={loading}
                                    pagination={pagination}
                                    onChange={handleTableChange}
                                />
                            </TabPane>
                            <TabPane tab="Blog" key="post">
                                <Table
                                    rowKey="id"
                                    columns={columns}
                                    dataSource={comments}
                                    loading={loading}
                                    pagination={pagination}
                                    onChange={handleTableChange}
                                />
                            </TabPane>
                            <TabPane tab="Training" key="trainingvideo">
                                <Table
                                    rowKey="id"
                                    columns={columns}
                                    dataSource={comments}
                                    loading={loading}
                                    pagination={pagination}
                                    onChange={handleTableChange}
                                />
                            </TabPane>
                        </Tabs>
                    </div>
                </Col>
            </Row>
        </Content>
    );
};

export default Comments;
